<template>
    <div class="flex items-center gap-2 control-group border-b hrmBorderColor"
        :class="editorConfigs?.isDisabled ? 'hrm-tiptap-disabled-button' : ''">
        <div class="button-group hrm-tip-button-group hrm-tip-tap-button-groups overflow-hidden">
            <div class="flex items-center gap-1 overflow-hidden w-full">
                <!-- Main Controls -->
                <div ref="toolbar" class="flex items-center gap-1 flex-nowrap w-full">
                    <ControlItem :controlItems="visibleControls" />
                </div>

                <!-- More Menu -->
                <div v-if="hiddenControls.length > 0">
                    <Popper class="hrm_common_chat_action_new" placement="bottom-end">
                        <hrm-button buttonMode="iconButton" iconSize="18px" buttonPadding="0" buttonSize="32px"
                            buttonClass="flex-none hrm-tip-tap-button flex justify-center items-center"
                            buttonAlign="center" icon="iconamoon:menu-kebab-vertical-circle-fill" tooltipText="Options"
                            tooltipPosition="top" />
                        <template #content="{ close }">
                            <div class="hrm-tiptap-card-menu tiptap-card-hidden-menu">
                                <div class="tiptap-card-heading-menu w-full">
                                    <ul class="w-full flex flex-col gap-1">
                                        <ControlItem :controlItems="hiddenControls" :isShowTitle="true" />
                                    </ul>
                                </div>
                            </div>
                        </template>
                    </Popper>
                </div>
            </div>
        </div>
    </div>
</template>
<script lang="ts" setup>
import { ref, Ref, inject, computed, onMounted, onUnmounted, onErrorCaptured, provide } from 'vue';

import Popper from 'vue3-popper';
import ControlItem from './ControlItem.vue';

// 
import { setFontColor } from '../Helpers/editorData';

// Types
import type { EditorConfig, ToolbarOption } from '../Helpers/types';
import { editorProps, fontMaps, aiActions } from '../Helpers/editorData';
import { EditorConfigsKey } from '../Helpers/keys';

// Inject with full typing
const editorConfigs = inject(
    EditorConfigsKey,
    ref<EditorConfig>(editorProps)
);

const tiptapEditor = inject<Ref<any>>("tiptapEditor", ref(null));
const toolbarOptions = inject<Ref<ToolbarOption[]>>("toolbarOptions")


type ControlType = "single" | "dropdown" | "styleDropDown" | "custom" | "clearStyle" | "userLinks";

interface ToolbarControl {
    id?: string;
    name?: string;
    icon?: string;
    tooltip?: string;
    type?: ControlType;
    iconSize?: string;
    styleType?: string;
    action?: ((event?: any) => void) | null
    isActive?: () => boolean | null;
    isDisabled?: boolean;
    options?: ToolbarControl[];
    style?: string | object;
    isText?: boolean,
    buttonClasses?: string;
    linkType?: string;
    dropdownClass?: string;
    toolbarId?: string;
    customSizeAction?: (event: any) => void;
}


// Example helpers
const isActiveButton = (type: string, attrs?: Record<string, any>) => {
    try {
        return tiptapEditor.value?.isActive(type, attrs ?? {}) ?? false;
    } catch {
        return false;
    }
};

const allControls = ref<ToolbarControl[]>([
    {
        id: "textStyle-bold",
        toolbarId: "bold",
        name: "Bold",
        icon: "ooui:bold-b",
        tooltip: "Bold",
        type: "single",
        iconSize: '14px',
        action: () => tiptapEditor.value?.chain().focus().toggleBold().run(),
    },
    {
        id: "textStyle-italic",
        toolbarId: "italic",
        name: "Italic",
        icon: "bx:italic",
        tooltip: "Italic",
        type: "single",
        iconSize: '18px',
        action: () => tiptapEditor.value?.chain().focus().toggleItalic().run(),
    },
    {
        id: "textStyle-strike",
        toolbarId: "strike",
        name: "Strike",
        icon: "mingcute:strikethrough-fill",
        tooltip: "Strike",
        type: "single",
        iconSize: '18px',
        action: () => tiptapEditor.value?.chain().focus().toggleStrike().run(),
    },

    // Paragraph
    {
        id: "textStyle-paragraph",
        toolbarId: "paragraph",
        name: "Paragraph",
        icon: "flowbite:paragraph-outline",
        tooltip: "Paragraph",
        type: "single",
        iconSize: '18px',
        action: () => tiptapEditor.value?.chain().focus().setParagraph().run(),
    },

    // Headings
    {
        id: "heading",
        toolbarId: "heading",
        name: "Heading",
        icon: "mdi:format-header-1",
        tooltip: "Heading",
        type: "dropdown",
        iconSize: '18px',
        options: [1, 2, 3, 4, 5, 6].map(level => ({
            id: `heading-${level}`,
            name: `Heading ${level}`,
            icon: `mdi:format-header-${level}`,
            tooltip: `Heading ${level}`,
            type: "single",
            iconSize: '18px',
            action: () => tiptapEditor.value?.chain().focus().setHeading({ level }).run(),
        })),
    },

    // Lists
    {
        id: "list",
        toolbarId: "list",
        name: "List",
        icon: "mdi:format-list-bulleted",
        tooltip: "Lists",
        type: "dropdown",
        dropdownClass: "hrm-tiptap-list",
        iconSize: '18px',
        options: [
            {
                id: "bulletList",
                name: "Bullet List",
                icon: "mdi:format-list-bulleted",
                tooltip: "Bullet List",
                type: "single",
                iconSize: '18px',
                action: () => tiptapEditor.value?.chain().focus().toggleBulletList().run(),
            },
            {
                id: "orderedList",
                name: "Ordered List",
                icon: "mdi:format-list-numbered",
                tooltip: "Ordered List",
                type: "single",
                iconSize: '18px',
                action: () => tiptapEditor.value?.chain().focus().toggleOrderedList().run(),
            },
            {
                id: "taskList",
                name: "Task List",
                icon: "mingcute:list-check-3-fill",
                tooltip: "Task List",
                type: "single",
                iconSize: '18px',
                action: () => tiptapEditor.value?.chain().focus().toggleTaskList().run(),
            },
        ],
    },

    // Highlight
    {
        id: "highlight",
        toolbarId: "highlight",
        name: "Highlight",
        icon: "fluent:highlight-24-filled",
        tooltip: "Highlight",
        type: "styleDropDown",
        styleType: 'highlight',
        iconSize: '18px',
        action: null, // top-level does nothing
        options: [
            // Custom color picker
            {
                id: "highlight-custom",
                name: "Custom Color",
                icon: "ic:round-color-lens",
                type: "custom",
                action: (event) => setHighLightColor(event),
                isActive: () => false,
            },

            {
                id: "highlight-#ffffff",
                name: "White",
                icon: "fa-solid:check",
                iconSize: '18px',
                type: "single",
                style: { 'backgroundColor': "#ffffff !important", color: `${setFontColor("#ffffff")} !important` },
                action: () => tiptapEditor.value?.chain().focus().toggleHighlight({ color: "#ffffff" }).run(),
                isActive: null,
            },
            {
                id: "highlight-#000000",
                name: "Black",
                icon: "fa-solid:check",
                type: "single",
                iconSize: '18px',
                style: { 'backgroundColor': "#000000 !important", color: `${setFontColor("#000000")} !important` },
                action: () => tiptapEditor.value?.chain().focus().toggleHighlight({ color: "#000000" }).run(),
                isActive: null,
            },
            {
                id: "highlight-#b95074",
                name: "Pink",
                icon: "fa-solid:check",
                type: "single",
                iconSize: '18px',
                style: { 'backgroundColor': "#b95074 !important", color: `${setFontColor("#b95074")} !important` },
                action: () => tiptapEditor.value?.chain().focus().toggleHighlight({ color: "#b95074" }).run(),
                isActive: null,
            },
            {
                id: "highlight-#278e5c",
                name: "Green",
                icon: "fa-solid:check",
                type: "single",
                iconSize: '18px',
                style: { 'backgroundColor': "#278e5c !important", color: `${setFontColor("#278e5c")} !important` },
                action: () => tiptapEditor.value?.chain().focus().toggleHighlight({ color: "#278e5c" }).run(),
                isActive: null,
            },
            {
                id: "highlight-#9c5c37",
                name: "Orange",
                icon: "fa-solid:check",
                type: "single",
                iconSize: '18px',
                style: { 'backgroundColor': "#9c5c37 !important", color: `${setFontColor("#9c5c37")} !important` },
                action: () => tiptapEditor.value?.chain().focus().toggleHighlight({ color: "#9c5c37" }).run(),
                isActive: null,
            },
            {
                id: "highlight-#6a2ea2",
                name: "Purple",
                icon: "fa-solid:check",
                type: "single",
                iconSize: '18px',
                style: { 'backgroundColor': "#6a2ea2 !important", color: `${setFontColor("#6a2ea2")} !important` },
                action: () => tiptapEditor.value?.chain().focus().toggleHighlight({ color: "#6a2ea2" }).run(),
                isActive: null,
            },
            {
                id: "highlight-#9d8732",
                name: "Yellow",
                icon: "fa-solid:check",
                type: "single",
                iconSize: '18px',
                style: { 'backgroundColor': "#9d8732 !important", color: `${setFontColor("#9d8732")} !important` },
                action: () => tiptapEditor.value?.chain().focus().toggleHighlight({ color: "#9d8732" }).run(),
                isActive: null,
            },

            // Clear highlight
            {
                id: "highlight-clear",
                name: "Clear Highlight",
                icon: "mdi:format-clear",
                type: "clearStyle",
                iconSize: '18px',
                action: () => tiptapEditor.value?.chain().focus().unsetHighlight().run(),
                isActive: () => false,
            },
        ],
    },

    // Font Color
    {
        id: "fontColor",
        toolbarId: "fontColor",
        name: "Font Color",
        icon: "flowbite:font-color-alt-solid",
        tooltip: "Font Color",
        type: "styleDropDown",
        styleType: 'textStyle',
        action: null, // top-level does nothing
        iconSize: '24px',
        options: [
            // Custom color picker
            {
                id: "fontColor-custom",
                name: "Custom Color",
                icon: "ic:round-color-lens",
                type: "custom",
                action: (event) => setEditorFontColor(event),
                iconSize: '18px',
            },

            {
                id: "fontColor-#ffffff",
                name: "White",
                icon: "fa-solid:check",
                type: "single",
                style: { 'backgroundColor': "#ffffff !important", color: setFontColor("#ffffff") },
                action: () => tiptapEditor.value?.chain().focus().setColor("#ffffff").run(),
                iconSize: '18px',
            },
            {
                id: "fontColor-#000000",
                name: "Black",
                icon: "fa-solid:check",
                type: "single",
                style: { 'backgroundColor': "#000000 !important", color: `${setFontColor("#000000")} !important` },
                action: () => tiptapEditor.value?.chain().focus().setColor("#000000").run(),
                iconSize: '18px',
            },
            {
                id: "fontColor-#b95074",
                name: "Pink",
                icon: "fa-solid:check",
                type: "single",
                style: { 'backgroundColor': "#b95074 !important", color: `${setFontColor("#b95074")} !important` },
                action: () => tiptapEditor.value?.chain().focus().setColor("#b95074").run(),
                iconSize: '18px',
            },
            {
                id: "fontColor-#278e5c",
                name: "Green",
                icon: "fa-solid:check",
                type: "single",
                style: { 'backgroundColor': "#278e5c !important", color: `${setFontColor("#278e5c")} !important` },
                action: () => tiptapEditor.value?.chain().focus().setColor("#278e5c").run(),
                iconSize: '18px',
            },
            {
                id: "fontColor-#9c5c37",
                name: "Orange",
                icon: "fa-solid:check",
                type: "single",
                style: { 'backgroundColor': "#9c5c37 !important", color: `${setFontColor("#9c5c37")} !important` },
                action: () => tiptapEditor.value?.chain().focus().setColor("#9c5c37").run(),
                iconSize: '18px',
            },
            {
                id: "fontColor-#6a2ea2",
                name: "Purple",
                icon: "fa-solid:check",
                type: "single",
                style: { 'backgroundColor': "#6a2ea2 !important", color: `${setFontColor("#6a2ea2")} !important` },
                action: () => tiptapEditor.value?.chain().focus().setColor("#6a2ea2").run(),
                iconSize: '18px',
            },
            {
                id: "fontColor-#9d8732",
                name: "Yellow",
                icon: "fa-solid:check",
                type: "single",
                style: { 'backgroundColor': "#9d8732 !important", color: `${setFontColor("#9d8732")} !important` },
                action: () => tiptapEditor.value?.chain().focus().setColor("#9d8732").run(),
                iconSize: '18px',
            },

            // Clear highlight
            {
                id: "fontColor-clear",
                name: "Clear Font Color",
                icon: "mdi:format-clear",
                type: "clearStyle",
                action: () => tiptapEditor.value?.chain().focus().unsetColor().run(),
                iconSize: '18px',
            },
        ],
    },

    {
        id: "fontSize",
        toolbarId: "fontSize",
        name: "Font Size",
        icon: "mdi-format-size",
        tooltip: "Font Size",
        type: "styleDropDown",
        buttonClasses: 'hrm-tip-tap-button hrm-tip-tap-font-size-button',
        iconSize: '18px',
        options: [
            // Predefined sizes
            {
                id: "fontSize-12",
                name: "12",
                icon: "mdi:format-size",
                type: "single",
                isText: true,
                iconSize: '18px',
                action: () => tiptapEditor.value?.chain().focus().setFontSize("12px").run(),
            },
            {
                id: "fontSize-14",
                name: "14",
                icon: "mdi:format-size",
                type: "single",
                isText: true,
                iconSize: '18px',
                action: () => tiptapEditor.value?.chain().focus().setFontSize("14px").run(),
            },
            {
                id: "fontSize-16",
                name: "16",
                icon: "mdi:format-size",
                type: "single",
                isText: true,
                iconSize: '18px',
                action: () => tiptapEditor.value?.chain().focus().setFontSize("16px").run(),
            },
            {
                id: "fontSize-18",
                name: "18",
                icon: "mdi:format-size",
                type: "single",
                isText: true,
                iconSize: '18px',
                action: () => tiptapEditor.value?.chain().focus().setFontSize("18px").run(),
            },
            {
                id: "fontSize-20",
                name: "20",
                icon: "mdi:format-size",
                type: "single",
                isText: true,
                action: () => tiptapEditor.value?.chain().focus().setFontSize("20px").run(),
            },
            {
                id: "fontSize-24",
                name: "24",
                icon: "mdi:format-size",
                type: "single",
                isText: true,
                iconSize: '18px',
                action: () => tiptapEditor.value?.chain().focus().setFontSize("24px").run(),
            },
            {
                id: "fontSize-32",
                name: "32",
                icon: "mdi:format-size",
                type: "single",
                isText: true,
                iconSize: '18px',
                action: () => tiptapEditor.value?.chain().focus().setFontSize("32px").run(),
            },

            // Clear font size
            {
                id: "fontSize-clear",
                name: "Clear",
                icon: "mdi:format-clear",
                type: "clearStyle",
                iconSize: '18px',
                action: () => tiptapEditor.value?.chain().focus().setFontSize(null).run(),
            }
        ],
        customSizeAction: (event) => {
            const value = event.target.value;
            tiptapEditor.value?.chain().focus().setFontSize(`${value}px`).run();
        },
    },

    {
        id: "fontFamily",
        toolbarId: "fontFamily",
        name: "Font Family",
        icon: "qlementine-icons:font-16",
        tooltip: "Font Family",
        type: "styleDropDown",
        buttonClasses: 'hrm-tip-tap-button hrm-tip-tap-font-family-button',
        iconSize: '18px',
        options: Object.entries(fontMaps).map(([fontName, fontValue]) => ({
            id: `fontFamily-${fontName.replace(/\s+/g, '-')}`,
            name: fontName,
            className: fontValue, // Added value for internal reference
            icon: "mdi:format-font",
            title: 'Aa',
            tooltip: `Font Family ${fontName}`,
            fontNameType: "Aa",
            type: "single",
            iconSize: '18px',
            action: () => tiptapEditor.value?.chain().focus().setFontFamily(fontName).run(),
        })),
    },

    // Link
    {
        id: "textStyle-link",
        toolbarId: "link",
        name: "Link",
        icon: "bx:link",
        tooltip: "Add Link",
        type: "userLinks",
        linkType: "link",
        iconSize: '18px',
        action: () => null,
    },
    {
        id: "math",
        toolbarId: "math",
        name: "Math",
        icon: "fluent:math-formula-16-filled",
        tooltip: "Insert Math",
        type: "dropdown",
        action: null,
        dropdownClass: "tiptap-card-table-menu",
        iconSize: '18px',
        options: [
            {
                id: "insert_inline",
                name: "Insert inline math",
                icon: "tabler:math-x-minus-x",
                action: () => applyMath("insert_inline"),
                iconSize: '18px',
                isActive: () => isActiveButton("insert_inline"),
            },
            {
                id: "remove_inline",
                name: "Remove inline math",
                icon: "tabler:math-off",
                action: () => removeMath("remove_inline"),
                iconSize: '18px',
                isActive: () => isActiveButton("remove_inline"),
            },
            {
                id: "insert_block",
                name: "Insert block math",
                icon: "mynaui:math-square-solid",
                action: () => applyMath("insert_block"),
                iconSize: '18px',
                isActive: () => isActiveButton("insert_block"),
            },
            {
                id: "remove_block",
                name: "Remove block math",
                icon: "tabler:math-function-off",
                action: () => removeMath("remove_block"),
                iconSize: '18px',
                isActive: () => isActiveButton("remove_block"),
            },
        ],
    },
    {
        id: "table",
        toolbarId: "table",
        name: "Table",
        icon: "streamline-plump:table-solid",
        tooltip: "Insert Table",
        type: "dropdown",
        iconSize: '16px',
        dropdownClass: "tiptap-card-table-menu",
        action: null, // top-level doesn't do anything, just a menu
        options: [
            {
                id: "add_table",
                name: "Insert Table",
                icon: "streamline-plump:table-solid",
                iconSize: '16px',
                action: () => addTable(),
            },
            {
                id: "delete_table",
                name: "Delete Table",
                icon: "mdi:table-remove",
                iconSize: '16px',
                action: () => deleteTable(),
            },
            {
                id: "add_row_before",
                name: "Add Row Before",
                icon: "mdi:table-row-plus-before",
                iconSize: '16px',
                action: () => addRowBefore(),
            },
            {
                id: "add_row_after",
                name: "Add Row After",
                icon: "mdi:table-row-plus-after",
                iconSize: '16px',
                action: () => addRowAfter(),
            },
            {
                id: "delete_row",
                name: "Delete Row",
                icon: "mdi:table-row-remove",
                iconSize: '16px',
                action: () => deleteRow(),
            },
            {
                id: "add_column_before",
                name: "Add Column Before",
                icon: "mdi:table-column-plus-before",
                iconSize: '16px',
                action: () => addColumnBefore(),
            },
            {
                id: "add_column_after",
                name: "Add Column After",
                icon: "mdi:table-column-plus-after",
                iconSize: '16px',
                action: () => addColumnAfter(),
            },
            {
                id: "delete_column",
                name: "Delete Column",
                icon: "mdi:table-column-remove",
                iconSize: '16px',
                action: () => deleteColumn(),
            },
            {
                id: "merge_cells",
                name: "Merge Cells",
                icon: "mdi:table-merge-cells",
                iconSize: '16px',
                action: () => mergeCells(),
            },
            {
                id: "split_cell",
                name: "Split Cell",
                icon: "mdi:table-split-cell",
                iconSize: '16px',
                action: () => splitCell(),
            },
        ],
    },

    // Other single type controls
    {
        id: "textStyle-code",
        toolbarId: "code",
        name: "Code",
        icon: "mingcute:code-fill",
        tooltip: "Inline Code",
        type: "single",
        iconSize: '18px',
        action: () => tiptapEditor.value?.chain().focus().toggleCode().run(),
    },
    {
        id: "textStyle-codeBlock",
        toolbarId: "codeBlock",
        name: "Code Block",
        icon: "mdi:code-block-tags",
        tooltip: "Code Block",
        type: "single",
        iconSize: '18px',
        action: () => tiptapEditor.value?.chain().focus().toggleCodeBlock().run(),
    },
    {
        id: "textStyle-blockquote",
        toolbarId: "blockquote",
        name: "Blockquote",
        icon: "mdi:format-quote-close",
        tooltip: "Blockquote",
        type: "single",
        iconSize: '18px',
        action: () => tiptapEditor.value?.chain().focus().toggleBlockquote().run(),
    },
    {
        id: "undo",
        toolbarId: "undo",
        name: "Undo",
        icon: "mdi:undo",
        tooltip: "Undo",
        type: "single",
        iconSize: '18px',
        action: () => tiptapEditor.value?.chain().focus().undo().run(),
    },
    {
        id: "redo",
        toolbarId: "redo",
        name: "Redo",
        icon: "mdi:redo",
        tooltip: "Redo",
        type: "single",
        iconSize: '18px',
        action: () => tiptapEditor.value?.chain().focus().redo().run(),
    },
    {
        id: "align",
        toolbarId: "align",
        name: "Text Align",
        icon: "mingcute:align-left-fill",
        tooltip: "Text Align",
        type: "dropdown",
        dropdownClass: "tiptap-card-table-menu",
        iconSize: '18px',
        options: [
            {
                id: "textStyle-left",
                name: "Align Left",
                icon: "mingcute:align-left-fill",
                tooltip: "Align Left",
                type: "single",
                iconSize: '16px',
                action: () => tiptapEditor.value?.chain().focus().setTextAlign('left').run(),
            },
            {
                id: "textStyle-center",
                name: "Align Center",
                icon: "mingcute:align-center-fill",
                tooltip: "Align Center",
                type: "single",
                iconSize: '16px',
                action: () => tiptapEditor.value?.chain().focus().setTextAlign('center').run(),
            },
            {
                id: "textStyle-right",
                name: "Align Right",
                icon: "mingcute:align-right-fill",
                tooltip: "Align Right",
                type: "single",
                iconSize: '16px',
                action: () => tiptapEditor.value?.chain().focus().setTextAlign('right').run(),
            },
            {
                id: "textStyle-justify",
                name: "Justify",
                icon: "mingcute:align-justify-fill",
                tooltip: "Justify",
                type: "single",
                iconSize: '16px',
                action: () => tiptapEditor.value?.chain().focus().setTextAlign('justify').run(),
            },
        ],
    },
    {
        id: "textStyle-subScript",
        toolbarId: "subScript",
        name: "Subscript",
        icon: "fa-solid:subscript",
        tooltip: "Subscript",
        type: "single",
        iconSize: '14px',
        action: () => tiptapEditor.value?.chain().focus().setSubscript().run(),
    },
    {
        id: "textStyle-supScript",
        toolbarId: "supScript",
        name: "Superscript",
        icon: "fa-solid:superscript",
        tooltip: "Superscript",
        type: "single",
        iconSize: '14px',
        action: () => tiptapEditor.value?.chain().focus().setSuperscript().run(),
    },
    {
        id: "aiGenerate",
        toolbarId: "aiGenerate",
        name: "AI Tools",
        icon: "mingcute:ai-fill",
        tooltip: "AI Generate Content",
        type: "dropdown",
        dropdownClass: "tiptap-card-table-menu",
        iconSize: '18px',
        options: aiActions.map(action => ({
            id: action.name.replace(/\s+/g, '').toLowerCase(),
            name: action.name,
            icon: action.icon,
            tooltip: action.ask_ai,
            type: "single",
            iconSize: '18px',
            action: () => helpInAiHelper(action.ask_ai),
        })),
    },
    {
        id: "youtube",
        toolbarId: "youtube",
        name: "YouTube",
        icon: "mdi:youtube",
        tooltip: "Insert YouTube Video",
        type: "userLinks",
        linkType: "youtube",
        iconSize: '22px',
        action: null,
    },
    {
        id: "clear-format",
        toolbarId: "clearFormat",
        name: "Clear Formatting",
        icon: "mdi:format-clear",
        tooltip: "Clear Formatting",
        type: "single",
        iconSize: '22px',
        action: () => tiptapEditor.value.chain().focus()
            .unsetAllMarks()
            .unsetColor()
            .unsetHighlight()
            .clearNodes()
            .run(),
    },
    {
        id: "add-signature-block",
        toolbarId: "signAdd",
        name: "Add Signature Block",
        icon: "lucide:signpost-big",
        tooltip: "Add Left / Right Block",
        type: "single",
        iconSize: '18px',
        action: () => insertSignatureBlock()
    },
]);

const filteredControls = computed(() => {
    const available = toolbarOptions.value;

    return allControls.value.filter(control => {
        const baseId = control?.toolbarId;
        return available.includes(baseId as ToolbarOption)
    })
})

const toolbar = ref(null);
const parentWidth = ref(0);

// Resize observer for toolbar parent
let observer;

onMounted(() => {
    if (toolbar.value?.parentElement) {
        observer = new ResizeObserver(() => {
            parentWidth.value = toolbar.value.parentElement.offsetWidth;
        });
        observer.observe(toolbar.value.parentElement);
    }
});

onUnmounted(() => {
    if (observer) observer.disconnect();
});

// computed visible / hidden controls
const visibleControls = computed(() => {
    if (!toolbar.value || parentWidth.value === 0) return filteredControls.value;

    let totalWidth = 0;
    const newVisible = [];
    const newHidden = [];

    for (let control of filteredControls.value) {
        // create a temporary element for measurement
        const test = document.createElement("button");
        test.className =
            "hrm_custom_icon_button hrm_button_hover buttongroup relative bg-hrmThemeBgColor text-hrmThemeTextColor rounded-lg hrm-tip-tap-button flex justify-center items-center is-active";
        test.style.visibility = "hidden";
        test.style.height = "28px";
        test.style.width = "28px";
        test.style.padding = "0px";
        test.textContent = control.name;

        toolbar.value.appendChild(test);
        const width = test.offsetWidth + 8; // +gap
        toolbar.value.removeChild(test);

        if (totalWidth + width < parentWidth.value - 50) {
            newVisible.push(control);
            totalWidth += width;
        } else {
            newHidden.push(control);
        }
    }
    return newVisible;
});

const hiddenControls = computed(() => {
    return filteredControls.value.filter(c => !visibleControls.value.includes(c));
});


// Set Highlight Color
const setHighLightColor = (event: any) => {
    tiptapEditor.value.chain().focus().toggleHighlight({ color: event.target.value }).run()
}

const setEditorFontColor = (event: any) => {
    tiptapEditor.value?.chain().focus().setColor(event.target.value).run();
}


// Apply Maths
// Math functions
const applyMath = (type: 'insert_block' | 'insert_inline') => {
    const fnMap = {
        insert_block: 'insertBlockMath',
        insert_inline: 'insertInlineMath',
    } as const

    const editor = tiptapEditor.value
    const { state } = editor
    const { from, to } = state.selection
    const selectedText = state.doc.textBetween(from, to, ' ')

    const latex = selectedText || 'E=mc^2'
    const fn = fnMap[type]

    return editor
        .chain()
        .focus()
        .deleteSelection() // <-- clears out selection
    [fn]({ latex }) // dynamically call correct fn
        .run()
}

const removeMath = (type: 'remove_block' | 'remove_inline') => {
    const fn = type === 'remove_block' ? 'deleteBlockMath' : 'deleteInlineMath'
    tiptapEditor.value
        .chain()
        .focus()
    [fn]()
        .run()
}


// Table Functions
const addTable = () => {
    tiptapEditor.value?.chain().focus().insertContent({
        type: "table",
        content: [
            // thead rows
            {
                type: "tableRow",
                content: Array.from({ length: 4 }).map(() => ({
                    type: "tableHeader",
                    content: [{ type: "paragraph" }],
                })),
            },
            // tbody rows
            {
                type: "tableRow",
                style: "width: 100%;",
                content: [{ type: "tableCell", content: [{ type: "paragraph" }] }],
            },
            {
                type: "tableRow",
                content: [{ type: "tableCell", content: [{ type: "paragraph" }] }],
            },
            {
                type: "tableRow",
                content: [{ type: "tableCell", content: [{ type: "paragraph" }] }],
            },
        ],
    }).run();
};

const deleteTable = () => {
    tiptapEditor.value?.chain().focus().deleteTable().run();
};

const addColumnBefore = () => {
    tiptapEditor.value?.chain().focus().addColumnBefore().run();
};
const addColumnAfter = () => {
    tiptapEditor.value?.chain().focus().addColumnAfter().run();
};
const deleteColumn = () => {
    tiptapEditor.value?.chain().focus().deleteColumn().run();
};

const addRowBefore = () => {
    tiptapEditor.value?.chain().focus().addRowBefore().run();
};
const addRowAfter = () => {
    tiptapEditor.value?.chain().focus().addRowAfter().run();
};
const deleteRow = () => {
    tiptapEditor.value?.chain().focus().deleteRow().run();
};

const mergeCells = () => {
    tiptapEditor.value?.chain().focus().mergeCells().run();
};
const splitCell = () => {
    tiptapEditor.value?.chain().focus().splitCell().run();
};

// Ai Helper Function 
const helpInAiHelper = (helperText: string) => {
    if (editorConfigs?.value?.isDisabled) return;
    try {
        tiptapEditor.value?.commands?.generateAIText(helperText);
    } catch (err) {
        console.error("Failed to invoke AI helper:", err)
    }
}

const insertSignatureBlock = () => {
    if (!tiptapEditor.value) return;
    tiptapEditor.value
        .chain()
        .focus()
        .insertContent({
            type: "table",
            attrs: {
                style: "min-width:100%; width:100%; border-collapse: collapse; table-layout: fixed;"   // ⭐ full width here
            },
            content: [
                {
                    type: "tableRow",
                    content: [
                        {
                            type: "tableCell",
                            attrs: {
                                style: "width:45%; min-width:45%; text-align:left; vertical-align:bottom;"
                            },
                            content: [
                                {
                                    type: "paragraph",
                                    content: [{ type: "text", text: "{hr_signature}" }],
                                },
                            ],
                        },

                        {
                            type: "tableCell",
                            attrs: {
                                style: "width:10%; min-width:10%;"
                            },
                            content: [{ type: "paragraph", content: [] }],
                        },

                        {
                            type: "tableCell",
                            attrs: {
                                style: "width:45%; min-width:45%; text-align:right; vertical-align:bottom;"
                            },
                            content: [
                                {
                                    type: "paragraph",
                                    content: [{ type: "text", text: "{signature}" }],
                                },
                            ],
                        },
                    ],
                },
            ],
        })
        .run();
};

onErrorCaptured((error) => {
    console.log(error)
})

provide('tiptapFunctions', {
    applyMath,
    removeMath,
    addTable,
    deleteTable,
    addColumnBefore,
    addColumnAfter,
    deleteColumn,
    addRowBefore,
    addRowAfter,
    deleteRow,
    mergeCells,
    splitCell,
    helpInAiHelper
});
</script>